import type { UserWithAggInfo } from "@/api/custom_types";
import {
	PasswordField,
	generatePassword,
} from "@/components/base/PasswordField";
import { useTranslation } from "@/hooks/useTranslation";
import { useAdminResetPassword } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	Icon,
	Input,
	Typography,
} from "@st-marketplace/ui";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

type ResetPasswordFormData = {
	new_password: string;
};

interface ResetPasswordDialogProps {
	open: boolean;
	user: UserWithAggInfo | null;
	onClose: () => void;
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
	open,
	user,
	onClose,
}) => {
	const { t } = useTranslation();
	const resetPasswordMutation = useAdminResetPassword();
	const loading = resetPasswordMutation.isPending;

	const [step, setStep] = useState<"form" | "confirm" | "success">("form");
	const [appliedPassword, setAppliedPassword] = useState("");
	const [revealed, setRevealed] = useState(false);
	const [copied, setCopied] = useState(false);
	const userId = user?.id;

	const resetSchema = useMemo(
		() =>
			z.object({
				new_password: z
					.string()
					.min(8)
					.max(64)
					.regex(
						/^(?=.*[a-z])(?=.*[A-Z]).+$/,
						t("validation.containUppercaseAndLowercase", {
							field: t("admin.users.resetPasswordNewLabel"),
						}),
					)
					.regex(
						/^(?=.*\d).+$/,
						t("validation.containNumber", {
							field: t("admin.users.resetPasswordNewLabel"),
						}),
					),
			}),
		[t],
	);

	const form = useForm<ResetPasswordFormData>({
		resolver: zodResolver(resetSchema),
		defaultValues: {
			new_password: generatePassword({ allowSymbols: false }),
		},
	});

	useEffect(() => {
		if (!userId) return;
		form.reset({ new_password: generatePassword({ allowSymbols: false }) });
		setStep("form");
		setAppliedPassword("");
		setRevealed(false);
		setCopied(false);
	}, [form, userId]);

	const handleClose = () => {
		if (loading) return;
		onClose();
	};

	const handleConfirm = () => {
		if (!user) return;
		const newPassword = form.getValues("new_password");
		resetPasswordMutation.mutate(
			{ body: { user_id: user.id, new_password: newPassword } },
			{
				onSuccess: () => {
					setAppliedPassword(newPassword);
					setRevealed(false);
					setCopied(false);
					setStep("success");
				},
			},
		);
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(appliedPassword);
			setCopied(true);
		} catch {
			setCopied(false);
			toast.error(t("error.unexpected"));
		}
	};

	const userLabel = user?.email ?? user?.username ?? "";

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="w-full max-w-120">
				<DialogHeader>
					<DialogTitle>{t("admin.users.resetPasswordTitle")}</DialogTitle>
				</DialogHeader>
				{step === "form" ? (
					<DialogScrollableArea className="py-0">
						<Typography className="mb-4 text-secondary-text-100">
							{t("admin.users.resetPasswordHint", { value: userLabel })}
						</Typography>
						<Form {...form}>
							<form
								noValidate
								onSubmit={form.handleSubmit(() => setStep("confirm"))}
								className="flex flex-col gap-4"
								id="admin-reset-password-form"
							>
								<PasswordField
									name="new_password"
									label={t("admin.users.resetPasswordNewLabel")}
									control={form.control}
									required
									autoGenerate
									allowSymbols={false}
									autoComplete="new-password"
									dir="ltr"
								/>
							</form>
						</Form>
					</DialogScrollableArea>
				) : step === "confirm" ? (
					<div className="px-4">
						<Typography className="mb-4 text-secondary-text-100">
							{t("admin.users.resetPasswordConfirmMessage", {
								value: userLabel,
							})}
						</Typography>
					</div>
				) : (
					<div className="flex flex-col gap-4 px-4">
						<Typography className="text-success-text">
							{t("admin.users.resetPasswordSuccess")}
						</Typography>
						<div className="flex items-center gap-2">
							<Input
								readOnly
								ltr
								value={appliedPassword}
								type={revealed ? "text" : "password"}
								autoComplete="off"
							/>
							<Button
								type="button"
								icon
								variant="text"
								color="ghost"
								size="sm"
								aria-label={revealed ? "Hide" : "Show"}
								onClick={() => setRevealed((v) => !v)}
							>
								<Icon name={revealed ? "eye-off-line" : "eye-line"} size="sm" />
							</Button>
							<Button type="button" size="sm" onClick={handleCopy}>
								{copied
									? t("admin.users.copiedOnboardingMessage")
									: t("admin.users.copyOnboardingMessage")}
							</Button>
						</div>
						<Typography className="text-secondary-text-100">
							{t("admin.users.resetPasswordSuccessHint", { value: userLabel })}
						</Typography>
					</div>
				)}
				<DialogFooter
					buttons={
						step === "form"
							? {
									secondary: {
										label: t("button.cancel"),
										props: { type: "button", onClick: handleClose },
									},
									primary: {
										label: t("button.submit"),
										props: {
											type: "submit",
											form: "admin-reset-password-form",
										},
									},
								}
							: step === "confirm"
								? {
										secondary: {
											label: t("common.button.no"),
											props: {
												type: "button",
												onClick: () => setStep("form"),
												disabled: loading,
											},
										},
										primary: {
											label: t("admin.users.resetPasswordConfirmButton"),
											props: {
												type: "button",
												loading,
												disabled: loading,
												onClick: handleConfirm,
											},
										},
									}
								: {
										primary: {
											label: t("admin.users.done"),
											props: { type: "button", onClick: handleClose },
										},
									}
					}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default ResetPasswordDialog;
